// src/hooks/useCurrentUser/selectors.ts
import { useMemo } from 'react';

import { useCurrentUser } from './index';
import { UserIdResult, UserNameResult, UserCompanyResult, UserSocialNetworksResult } from './types';

/**
 * Selector hook for user ID only - minimizes re-renders
 */
export function useUserId(): UserIdResult {
  const { user } = useCurrentUser();
  return useMemo(() => user?.id ?? null, [user?.id]);
}

/**
 * Selector hook for user name fields
 */
export function useUserName(): UserNameResult {
  const { user } = useCurrentUser();

  return useMemo(() => {
    if (!user) return null;

    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');

    return { firstName, lastName, fullName };
  }, [user?.first_name, user?.last_name]);
}

/**
 * Selector hook for user's company data
 */
export function useUserCompany(): UserCompanyResult {
  const { user } = useCurrentUser();
  return useMemo(() => user?.company ?? null, [user?.company]);
}

/**
 * Selector hook for user's social networks from /me
 */
export function useUserSocialNetworksFromMe(): UserSocialNetworksResult {
  const { user } = useCurrentUser();
  return useMemo(() => user?.social_networks ?? [], [user?.social_networks]);
}

/**
 * Selector hook for user's organization data
 */
export function useUserOrganization() {
  const { user } = useCurrentUser();
  return useMemo(() => user?.user_to_organization?.[0] ?? null, [user?.user_to_organization]);
}

/**
 * Selector hook for user's organization UUID
 * @returns Organization UUID or null if not available
 */
export function useUserOrganizationUuid(): string | null {
  const { user } = useCurrentUser();
  return useMemo(
    () => user?.user_to_organization?.[0]?.organization?.uuid ?? null,
    [user?.user_to_organization]
  );
}

/**
 * Selector hook for advisor status
 */
export function useIsAdvisor(): boolean {
  const { user } = useCurrentUser();
  return useMemo(() => user?.is_advisor ?? false, [user?.is_advisor]);
}

/**
 * Selector hook for user's email address
 * @returns User email or null if not available
 */
export function useUserEmail(): string | null {
  const { user } = useCurrentUser();
  return useMemo(() => user?.email ?? null, [user?.email]);
}

/**
 * Selector hook for user's phone number (WhatsApp)
 * @returns User phone number or null if not available
 */
export function useUserPhone(): string | null {
  const { user } = useCurrentUser();
  return useMemo(() => user?.whatsapp_number ?? null, [user?.whatsapp_number]);
}

/**
 * Selector hook for user's image URL from user data
 * Note: For avatar display, prefer useUserAvatar from hooks/useUserAvatar
 * which provides optimized batch fetching.
 * @returns User image URL or null if not available
 */
export function useUserImageUrl(): string | null {
  const { user } = useCurrentUser();
  return useMemo(() => user?.image_url ?? null, [user?.image_url]);
}

/**
 * Selector hook for user's person ID (used for copilot and other services)
 * @returns User person_id or null if not available
 */
export function useUserPersonId(): string | null {
  const { user } = useCurrentUser();
  return useMemo(() => user?.person_id ?? null, [user?.person_id]);
}

/**
 * Selector hook for user's company UUID
 * @returns User company UUID or null if not available
 */
export function useUserCompanyUuid(): string | null {
  const { user } = useCurrentUser();
  return useMemo(() => user?.company?.uuid ?? null, [user?.company?.uuid]);
}

/**
 * Selector hook for user's company role/position
 * @returns User company role or null if not available
 */
export function useUserCompanyRole(): string | null {
  const { user } = useCurrentUser();
  return useMemo(() => user?.company_role ?? null, [user?.company_role]);
}

/**
 * Selector hook for user's avatar raca
 * @returns User avatar raca or null if not available
 */
export function useUserAvatarRaca(): string | null {
  const { user } = useCurrentUser();
  return useMemo(() => user?.avatar_raca ?? null, [user?.avatar_raca]);
}

/**
 * Selector hook for user's arquetipo
 * @returns User arquetipo or null if not available
 */
export function useUserArquetipo(): string | null {
  const { user } = useCurrentUser();
  return useMemo(() => user?.arquetipo ?? null, [user?.arquetipo]);
}

/**
 * Selector hook for user's personalidade
 * @returns User personalidade or null if not available
 */
export function useUserPersonalidade(): string | null {
  const { user } = useCurrentUser();
  return useMemo(() => user?.personalidade ?? null, [user?.personalidade]);
}

/**
 * Selector hook for user's caracteristicas
 * @returns User caracteristicas or null if not available
 */
export function useUserCaracteristicas(): string | null {
  const { user } = useCurrentUser();
  return useMemo(() => user?.caracteristicas ?? null, [user?.caracteristicas]);
}
